import { useEffect } from 'react';
import { CreditType } from '@perxis-auto/credit-instock';
import { useCreditChoice } from '@perxis-auto/credit-sdk';

import { useAppDispatch } from '@/features/hooks';
import { rootActions } from '@/features/store';
import isDefined from '@/utils/isDefined';

import { CreditDataFetchProps } from '../types';

import useCreditData from './useCreditData';
import useCreditDataFetch from './useCreditDataFetch';

export default function useCreditDataCard({
  carId,
  model = '',
  price = 0,
}: CreditDataFetchProps) {
  const dispatch = useAppDispatch();

  const {
    data: choice,
    setCar,
    setCreditParameters,
    setFinancialProgram,
  } = useCreditChoice();
  const creditData = useCreditData(carId);
  const fetchCreditData = useCreditDataFetch({
    carId,
    model,
    price,
  });

  const isCreditProgramExist = isDefined(creditData?.choice?.program);
  const isCreditParametersExist =
    isDefined(creditData?.choice?.type) &&
    isDefined(creditData?.choice?.downPayment) &&
    isDefined(creditData?.choice?.numberOfPeriods);

  useEffect(() => {
    setCar({
      model,
      price,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, model]);

  useEffect(() => {
    if (
      isDefined(price) &&
      isDefined(creditData?.choice?.price) &&
      price !== creditData?.choice?.price
    ) {
      void fetchCreditData();
    }
  }, [price, creditData, fetchCreditData]);

  useEffect(() => {
    if (isCreditParametersExist) {
      setCreditParameters({
        type: creditData.choice.type,
        downPayment: creditData.choice.downPayment,
        residualPayment: creditData?.choice?.residualPayment ?? null,
        numberOfPeriods: creditData.choice.numberOfPeriods,
      });
    }
    if (isCreditProgramExist) {
      setFinancialProgram({
        program: creditData.choice.program,
      });
    }
    if (!creditData) {
      setCreditParameters({
        type: CreditType.Annuity,
        downPayment: 50,
        residualPayment: null,
        numberOfPeriods: 48,
      });
      setFinancialProgram({
        program: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreditParametersExist, isCreditProgramExist, creditData]);

  useEffect(() => {
    if (!creditData) {
      void fetchCreditData();
    }
  }, [carId, creditData, fetchCreditData]);

  useEffect(() => {
    if (
      isDefined(carId) &&
      isDefined(choice) &&
      isDefined(creditData) &&
      isDefined(creditData.choice) &&
      creditData.choice !== choice
    ) {
      dispatch(
        rootActions.credit.addCredit({
          carId,
          choice,
          previewProgram: creditData.previewProgram,
          isSelected: isDefined(choice.program),
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choice]);

  return creditData;
}
